import { buildApiUrl } from "../../config.js";
import React, { useEffect, useState } from "react";
import MobileOrderCard from "./MobileOrderCard";
import CreateOrderModal from "./CreateOrderModal"; // Import the CreateOrderModal component
import { ToastContainer, toast } from "react-toastify"; // Import toast notifications
import "react-toastify/dist/ReactToastify.css"; // Import toast CSS
import { FaEdit, FaFilePdf, FaTrash } from "react-icons/fa";
import EditOrderModal from "./EditOrderModal";
import SearchBar from "./SearchBar";
import Pagination from "./Pagination";
import { ColorRing } from "react-loader-spinner";
import { Plus } from "lucide-react";

const OrdersTable = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState(null); // Order data to be edited
  const [selectedTab, setSelectedTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 50;
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false); // Modal visibility for rejection
  const [rejectReason, setRejectReason] = useState(""); // Reason for rejecting
  const [orderToReject, setOrderToReject] = useState(null); // Order to be rejected

  const token = localStorage.getItem("token");
  const userRole = localStorage.getItem("role")?.toLowerCase(); // Get role and convert to lowercase for comparison

  const fetchOrders = async () => {
    try {
      const response = await fetch(buildApiUrl("/api/orderItem/get"), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch orders");
      }

      const data = await response.json();
      setOrders(data.orders);

      const initialExpandedState = data.orders.reduce((acc, order) => {
        acc[order._id] = false;
        return acc;
      }, {});

      setExpandedItems(initialExpandedState);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [token]);

  const getActionClass = (status) => {
    switch (status) {
      case "Pending":
        return "bg-yellow-200 text-yellow-800";
      case "Accept":
        return "bg-green-200 text-green-800";
      case "Reject":
        return "bg-red-200 text-red-800";
      default:
        return "bg-gray-200 text-gray-800";
    }
  };

  const toggleItemRow = (orderId) => {
    setExpandedItems((prevState) => {
      // If the clicked modal is already open, close it by returning an empty object.
      if (prevState[orderId]) {
        return {};
      }
      // Otherwise, open only this modal.
      return { [orderId]: true };
    });
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };
  const openEditModal = (order) => {
    setOrderToEdit(order);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setOrderToEdit(null);
  };

  const handleOrderCreated = async (newOrder) => {
    await fetchOrders();
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      const response = await fetch(
        buildApiUrl(`/api/orderItem/${orderId}`),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete order");
      }

      setOrders((prevOrders) =>
        prevOrders.filter((order) => order._id !== orderId)
      );
      toast.success("Order deleted successfully!");
    } catch (error) {
      toast.error("Error: " + error.message);
    }
  };

  const filteredByTab = orders.filter((order) => {
    console.log("order tpt status", order.tpt?.tptStatus);
    if (selectedTab === "all") return true;
    if (selectedTab === "accept") {
      return (
        order.status === "Accept" && order.orderDeliveredStatus != "Dispatched"
      );
    }
    if (selectedTab === "waitingForTpt") {
      return (
        order.status === "Accept" &&
        order.orderDeliveredStatus === "Dispatched" &&
        order.tpt?.tptStatus === "Pending"
      );
    }

    if (selectedTab === "dispatched") {
      return (
        order.status === "Accept" &&
        order.orderDeliveredStatus === "Dispatched" &&
        order.tpt?.tptStatus !== "Pending"
      );
    }

    return order.status.toLowerCase() === selectedTab.toLowerCase();
  });

  // Filter orders by search query
  const filteredOrders = filteredByTab.filter((order) => {
    const queryLower = searchQuery.toLowerCase();
    return (
      order._id.toLowerCase().includes(queryLower) ||
      order.status.toLowerCase().includes(queryLower) ||
      order.items.some(
        (item) =>
          item.categoryName.toLowerCase().includes(queryLower) ||
          item.itemName.toLowerCase().includes(queryLower)
      )
    );
  });

  // Sorting orders by status: Pending, Reject, Accept, and by latest created
  const sortedOrders = filteredOrders.sort((a, b) => {
    const statusOrder = ["Pending", "Reject", "Accept"];
    const statusComparison =
      statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
    if (statusComparison === 0) {
      return new Date(b.createdAt) - new Date(a.createdAt);
    }
    return statusComparison;
  });

  const handleUpdateStatus = async (orderId, newStatus) => {
    // Find the order to check if amount is set
    const order = orders.find((o) => o._id === orderId);
    
    // Prevent status update if amount is not set (except for Pending status)
    if (order && order.totalItemAmount <= 0 && newStatus !== "Pending") {
      toast.error("Cannot update status. Total amount is not set yet.");
      return;
    }

    if (newStatus === "Reject") {
      // Open the modal for rejecting the order and ask for a reason
      setOrderToReject(orderId); // Store the order ID to be rejected
      setIsRejectModalOpen(true); // Open the rejection modal
    } else {
      // Handle other statuses (Accept or Pending)
      try {
        const response = await fetch(
          buildApiUrl(`/api/orderItem/accept-reject/${orderId}`),
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ status: newStatus }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update status");
        }

        // Update the order in the state if the status update is successful
        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order._id === orderId ? { ...order, status: newStatus } : order
          )
        );

        toast.success("Order status updated successfully!");
      } catch (error) {
        toast.error("Error: " + error.message);
      }
    }
  };

  const handleRejectOrder = async () => {
    if (!rejectReason.trim()) {
      // If the rejection reason is empty, show an error message
      toast.error("Please provide a reason for rejection.");
      return; // Prevent submission if the reason is empty
    }

    try {
      const response = await fetch(
        buildApiUrl(`/api/orderItem/accept-reject/${orderToReject}`),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: "Reject", reason: rejectReason }), // Send the reason
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to reject order");
      }

      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order._id === orderToReject ? { ...order, status: "Reject" } : order
        )
      );

      toast.success("Order rejected successfully!");
      setIsRejectModalOpen(false); // Close the modal after rejection
      setRejectReason(""); // Clear the reason input
    } catch (error) {
      toast.error("Error: " + error.message);
    }
  };

  const closeRejectModal = () => {
    setIsRejectModalOpen(false);
    setRejectReason(""); // Clear the reason input when modal is closed
  };

  const orderCounts = {
    all: orders.length,
    pending: orders.filter((order) => order.status?.toLowerCase() === "pending")
      .length,
    accept: orders.filter(
      (order) =>
        order.status?.toLowerCase() === "accept" &&
        order.orderDeliveredStatus != "Dispatched"
    ).length,
    reject: orders.filter((order) => order.status?.toLowerCase() === "reject")
      .length,
    waitingForTpt: orders.filter(
      (order) =>
        order.status === "Accept" &&
        order.orderDeliveredStatus === "Dispatched" &&
        order.tpt?.tptStatus === "Pending"
    ).length,
    dispatched: orders.filter(
      (order) =>
        order.status === "Accept" &&
        order.orderDeliveredStatus === "Dispatched" &&
        order.tpt?.tptStatus !== "Pending"
    ).length,
  };

  // Pagination Logic
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = sortedOrders.slice(indexOfFirstOrder, indexOfLastOrder); // Use sorted orders for pagination

  // Calculate total pages
  const totalPages = Math.ceil(sortedOrders.length / ordersPerPage);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <ColorRing
          visible={true}
          height="120"
          width="120"
          ariaLabel="color-ring-loading"
          wrapperStyle={{}}
          wrapperClass="color-ring-wrapper"
          colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex items-center max-w-md mx-auto my-4 p-4 bg-red-100 border-l-4 border-red-500 text-red-700 rounded shadow-md"
        role="alert"
      >
        {/* Error Icon */}
        <svg
          className="w-6 h-6 mr-3 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M18.364 5.636l-12.728 12.728M5.636 5.636l12.728 12.728"
          />
        </svg>

        {/* Error Message */}
        <div>
          <p className="font-bold">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div
        className="sm:max-h-[100vh] overflow-y-auto"
        style={{ scrollbarWidth: "none" }}
      >
        <div className="sticky top-0 z-30 bg-white py-1 sm:shadow-md">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-extrabold text-gray-900 tracking-tight">
              Orders Dashboard
            </h2>
            <button
              onClick={openModal}
              className="inline-flex items-center px-3 py-2 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Order
            </button>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between">
            <div className="flex flex-wrap gap-2 mb-4 sm:mb-0">
              {[
                {
                  id: "all",
                  label: "All",
                  count: orderCounts.all,
                  color: "blue",
                },
                {
                  id: "pending",
                  label: "Pending",
                  count: orderCounts.pending,
                  color: "gray",
                },
                {
                  id: "accept",
                  label: "Accepted",
                  count: orderCounts.accept,
                  color: "green",
                },
                {
                  id: "reject",
                  label: "Reject",
                  count: orderCounts.reject,
                  color: "red",
                },
                {
                  id: "waitingForTpt",
                  label: "Waiting for TPT",
                  count: orderCounts.waitingForTpt,
                  color: "gray",
                },
                {
                  id: "dispatched",
                  label: "All Clear",
                  count: orderCounts.dispatched,
                  color: "green",
                },
              ].map((tab) => (
                <button
                  key={tab.id}
                  className={`py-1 px-2 rounded-lg text-[11px] font-medium ${
                    selectedTab === tab.id
                      ? `bg-${tab.color}-500 text-white`
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  } transition duration-200`}
                  onClick={() => setSelectedTab(tab.id)}
                >
                  {tab.label}
                  <span
                    className={`ml-2 text-[11px] bg-${tab.color}-100 text-${tab.color}-800 rounded-full px-2`}
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
            <SearchBar onSearch={(query) => setSearchQuery(query)} />
          </div>
        </div>
        <div className="overflow-x-auto rounded-md">
          <table className="table-auto w-full border-collapse hidden sm:table">
            <tbody>
              {currentOrders.map((order, orderIndex) => (
                <React.Fragment key={order._id}>
                  <tr
                    className={`border-b bg-white hover:bg-gray-100 ${
                      expandedItems[order._id] &&
                      "!bg-red-50  border border-gray-300"
                    }`}
                  >
                    <td colSpan="9" className="p-2">
                      <div className="flex justify-between items-center">
                        <div>
                          <strong className="text-gray-800 uppercase">
                            Order ID:{" "}
                            <span className="font-semibold text-indigo-600 text-[10px] bg-blue-100 p-1 ml-2 uppercase rounded-md">
                              {order._id.slice(-5)}
                            </span>
                          </strong>
                          {/* {order.totalItemAmount === 0 && (
                            <span className="bg-green-500 text-white font-semibold ml-2 p-1 rounded text-[10px]">
                              New Order
                            </span>
                          )} */}
                          {order.totalItemAmount === 0 && (
                            <span className="bg-red-100 text-red-800 font-semibold ml-2 px-2 py-1 rounded-full text-[10px]">
                              Under Processing
                            </span>
                          )}
                          <div className="text-xs text-gray-600 mt-1">
                            Created By -{" "}
                            <span className="font-medium">
                              {order.createdBy
                                ? order.createdBy.name
                                : "Unknown"}
                            </span>{" "}
                            <br />
                            Assigned To -{" "}
                            <span className="font-medium">
                              {order.assignedTo
                                ? order.assignedTo.name
                                : "Not Assigned"}
                            </span>{" "}
                            <br />
                            Total Amount -{" "}
                            <span className="font-medium">
                              {order.totalItemAmount}
                            </span>
                          </div>
                          {order.rejectionReason && (
                            <div className="text-xs text-red-600 mt-1">
                              Rejection Reason -{" "}
                              <span className="font-bold">
                                {order.rejectionReason}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-4">
                          <div>
                            <FaFilePdf
                              size={14}
                              className="text-red-600 hover:text-red-800 cursor-pointer"
                            />
                          </div>
                          <div
                            className={`px-3 py-1 font-medium rounded-md text-xs ${getActionClass(
                              order.status
                            )}`}
                          >
                            {order.status}
                          </div>
                          {order.totalItemAmount > 0 &&
                            (order.status === "Pending" ||
                              order.status === "Reject") && (
                              <select
                                className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-1 text-xs hover:bg-gray-200 transition duration-200"
                                onChange={(e) =>
                                  handleUpdateStatus(order._id, e.target.value)
                                }
                                defaultValue={order.status}
                              >
                                <option value="" disabled>
                                  Select Status
                                </option>
                                <option value="Pending">Pending</option>
                                <option value="Accept">Accept</option>
                                <option value="Reject">Reject</option>
                              </select>
                            )}
                          {order.status === "Pending" && (
                            <button
                              onClick={() => openEditModal(order)}
                              className="text-blue-500 hover:text-blue-700"
                            >
                              <FaEdit size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>

                  {/* Serial Number Row */}
                  <tr
                    className={`border-b border-gray-300 bg-white hover:bg-gray-100 ${
                      expandedItems[order._id] &&
                      "!bg-red-50 border border-gray-300"
                    }`}
                  >
                    <td className="px-2 py-1">{orderIndex + 1}</td>
                    <td colSpan="6">
                      <button
                        onClick={() => toggleItemRow(order._id)}
                        className="text-indigo-600 hover:text-indigo-800 text-xs font-medium transform hover:scale-105 transition duration-200"
                      >
                        {expandedItems[order._id]
                          ? "Hide Details"
                          : "Show Details"}
                      </button>
                    </td>
                  </tr>

                  {expandedItems[order._id] && (
                    <tr className="border border-gray-300">
                      <td colSpan="7" className="p-0">
                        <table className="table-auto w-full border-collapse">
                          <thead>
                            <tr className="text-left bg-gray-100 text-sm text-gray-600">
                              <th className="px-2 py-1 font-medium text-center">
                                #
                              </th>
                              <th className="px-2 py-1 font-medium text-center">
                                Category
                              </th>
                              <th className="px-2 py-1 font-medium text-center">
                                Product Items
                              </th>
                              <th className="px-2 py-1 font-medium text-center">
                                Quantity
                              </th>
                              {/* Show Warehouse Name and Warehouse Batch only for non-client roles */}
                              {userRole && userRole !== "client" && (
                                <>
                                  <th className="px-2 py-1 font-medium text-center">
                                    Warehouse Name
                                  </th>
                                  <th className="px-2 py-1 font-medium text-center">
                                    Warehouse Batch
                                  </th>
                                </>
                              )}
                              <th className="px-2 py-1 font-medium text-center">
                                Price
                              </th>
                              <th className="px-2 py-1 font-medium text-center">
                                Total Amount
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {order.items.map((item, itemIndex) => (
                              <tr key={item._id} className="border-b text-sm">
                                <td className="px-2 py-0.5 text-center">
                                  {itemIndex + 1}
                                </td>
                                <td className="px-2 py-0.5 text-center">
                                  {item.categoryName || "-"}
                                </td>
                                <td className="px-2 py-0.5 text-center">
                                  {item.itemName || "-"}
                                </td>
                                <td className="px-2 py-0.5 text-center">
                                  {item.qty || "-"}
                                </td>
                                {/* Show Warehouse Name and Warehouse Batch only for non-client roles */}
                                {userRole && userRole !== "client" && (
                                  <>
                                    <td className="px-2 py-0.5 text-center">
                                      {item.wareHouseName || "-"}
                                    </td>
                                    <td className="px-2 py-0.5 text-center">
                                      {item.inwardReference || "-"}
                                    </td>
                                  </>
                                )}
                                <td className="px-2 py-0.5 text-center">
                                  {item.price ? `${item.price}` : "-"}
                                </td>
                                <td className="px-2 py-0.5 text-center">
                                  {item.totalAmount
                                    ? `${item.totalAmount}`
                                    : "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <div className="block sm:hidden">
            {currentOrders.map((order) => (
              <MobileOrderCard
                key={order._id}
                order={order}
                getActionClass={getActionClass}
                openEditModal={openEditModal}
                handleDeleteOrder={handleDeleteOrder}
                handleUpdateStatus={handleUpdateStatus}
              />
            ))}
          </div>
        </div>
      </div>

      {isRejectModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-1/3">
            <h3 className="text-xl font-semibold mb-4">
              Provide a Reason for Rejection
            </h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full p-2 border border-gray-300 rounded-lg mb-4"
            />
            <div className="flex justify-end space-x-4">
              <button
                onClick={closeRejectModal}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectOrder}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200"
              >
                Reject Order
              </button>
            </div>
          </div>
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
      />
      <ToastContainer />
      {isModalOpen && (
        <CreateOrderModal
          closeModal={closeModal}
          onOrderCreated={handleOrderCreated}
        />
      )}
      {isEditModalOpen && (
        <EditOrderModal
          order={orderToEdit}
          closeModal={closeEditModal}
          onOrderUpdated={handleOrderCreated}
        />
      )}
    </div>
  );
};

export default OrdersTable;

// import React, { useEffect, useState } from "react";
// import MobileOrderCard from "./MobileOrderCard";
// import CreateOrderModal from "./CreateOrderModal"; // Import the CreateOrderModal component
// import { ToastContainer, toast } from "react-toastify"; // Import toast notifications
// import "react-toastify/dist/ReactToastify.css"; // Import toast CSS
// import { FaEdit, FaFilePdf, FaTrash } from "react-icons/fa";
// import EditOrderModal from "./EditOrderModal";
// import SearchBar from "./SearchBar";
// import Pagination from "./Pagination";
// import { ColorRing } from "react-loader-spinner";

// const OrdersTable = () => {
//   const [orders, setOrders] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [expandedItems, setExpandedItems] = useState({});
//   const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility
//   const [isEditModalOpen, setIsEditModalOpen] = useState(false);
//   const [orderToEdit, setOrderToEdit] = useState(null); // Order data to be edited
//   const [selectedTab, setSelectedTab] = useState("all");
//   const [searchQuery, setSearchQuery] = useState("");
//   const [currentPage, setCurrentPage] = useState(1);
//   const ordersPerPage = 50;
//   const [isRejectModalOpen, setIsRejectModalOpen] = useState(false); // Modal visibility for rejection
//   const [rejectReason, setRejectReason] = useState(""); // Reason for rejecting
//   const [orderToReject, setOrderToReject] = useState(null); // Order to be rejected

//   const token = localStorage.getItem("token");

//   const fetchOrders = async () => {
//     try {
//       const response = await fetch(buildApiUrl("/api/orderItem/get"), {
//         method: "GET",
//         headers: {
//           Authorization: `Bearer ${token}`,
//           "Content-Type": "application/json",
//         },
//       });

//       if (!response.ok) {
//         throw new Error("Failed to fetch orders");
//       }

//       const data = await response.json();
//       setOrders(data.orders);

//       const initialExpandedState = data.orders.reduce((acc, order) => {
//         acc[order._id] = false;
//         return acc;
//       }, {});

//       setExpandedItems(initialExpandedState);
//     } catch (error) {
//       setError(error.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchOrders();
//   }, [token]);

//   const getActionClass = (status) => {
//     switch (status) {
//       case "Pending":
//         return "bg-yellow-200 text-yellow-800";
//       case "Accept":
//         return "bg-green-200 text-green-800";
//       case "Reject":
//         return "bg-red-200 text-red-800";
//       default:
//         return "bg-gray-200 text-gray-800";
//     }
//   };

//   // const toggleItemRow = (orderId) => {
//   //   setExpandedItems((prevState) => ({
//   //     ...prevState,
//   //     [orderId]: !prevState[orderId],
//   //   }));
//   // };

//   const toggleItemRow = (orderId) => {
//     setExpandedItems((prevState) => {
//       // If the clicked modal is already open, close it by returning an empty object.
//       if (prevState[orderId]) {
//         return {};
//       }
//       // Otherwise, open only this modal.
//       return { [orderId]: true };
//     });
//   };

//   const openModal = () => {
//     setIsModalOpen(true);
//   };

//   const closeModal = () => {
//     setIsModalOpen(false);
//   };
//   const openEditModal = (order) => {
//     setOrderToEdit(order);
//     setIsEditModalOpen(true);
//   };

//   const closeEditModal = () => {
//     setIsEditModalOpen(false);
//     setOrderToEdit(null);
//   };

//   const handleOrderCreated = async (newOrder) => {
//     await fetchOrders();
//   };

//   const handleDeleteOrder = async (orderId) => {
//     try {
//       const response = await fetch(
//         `buildApiUrl("/api/orderItem/${orderId}`,
//         {
//           method: "DELETE",
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//           },
//         }
//       );

//       if (!response.ok) {
//         throw new Error("Failed to delete order");
//       }

//       setOrders((prevOrders) =>
//         prevOrders.filter((order) => order._id !== orderId)
//       );
//       toast.success("Order deleted successfully!");
//     } catch (error) {
//       toast.error("Error: " + error.message);
//     }
//   };

//   const filteredByTab = orders.filter((order) => {
//     console.log("order tpt status", order.tpt?.tptStatus);
//     if (selectedTab === "all") return true;
//     if (selectedTab === "accept") {
//       return (
//         order.status === "Accept" && order.orderDeliveredStatus != "Dispatched"
//       );
//     }
//     if (selectedTab === "waitingForTpt") {
//       return (
//         order.status === "Accept" &&
//         order.orderDeliveredStatus === "Dispatched" &&
//         order.tpt?.tptStatus === "Pending"
//       );
//     }

//     if (selectedTab === "dispatched") {
//       return (
//         order.status === "Accept" &&
//         order.orderDeliveredStatus === "Dispatched" &&
//         order.tpt?.tptStatus !== "Pending"
//       );
//     }

//     return order.status.toLowerCase() === selectedTab.toLowerCase();
//   });

//   // Filter orders by search query
//   const filteredOrders = filteredByTab.filter((order) => {
//     const queryLower = searchQuery.toLowerCase();
//     return (
//       order._id.toLowerCase().includes(queryLower) ||
//       order.status.toLowerCase().includes(queryLower) ||
//       order.items.some(
//         (item) =>
//           item.categoryName.toLowerCase().includes(queryLower) ||
//           item.itemName.toLowerCase().includes(queryLower)
//       )
//     );
//   });

//   // Sorting orders by status: Pending, Reject, Accept, and by latest created
//   const sortedOrders = filteredOrders.sort((a, b) => {
//     const statusOrder = ["Pending", "Reject", "Accept"];
//     const statusComparison =
//       statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
//     if (statusComparison === 0) {
//       return new Date(b.createdAt) - new Date(a.createdAt);
//     }
//     return statusComparison;
//   });

//   const handleUpdateStatus = async (orderId, newStatus) => {
//     if (newStatus === "Reject") {
//       // Open the modal for rejecting the order and ask for a reason
//       setOrderToReject(orderId); // Store the order ID to be rejected
//       setIsRejectModalOpen(true); // Open the rejection modal
//     } else {
//       // Handle other statuses (Accept or Pending)
//       try {
//         const response = await fetch(
//           `buildApiUrl("/api/orderItem/accept-reject/${orderId}`,
//           {
//             method: "PUT",
//             headers: {
//               Authorization: `Bearer ${token}`,
//               "Content-Type": "application/json",
//             },
//             body: JSON.stringify({ status: newStatus }),
//           }
//         );

//         if (!response.ok) {
//           const errorData = await response.json();
//           throw new Error(errorData.message || "Failed to update status");
//         }

//         // Update the order in the state if the status update is successful
//         setOrders((prevOrders) =>
//           prevOrders.map((order) =>
//             order._id === orderId ? { ...order, status: newStatus } : order
//           )
//         );

//         toast.success("Order status updated successfully!");
//       } catch (error) {
//         toast.error("Error: " + error.message);
//       }
//     }
//   };

//   const handleRejectOrder = async () => {
//     if (!rejectReason.trim()) {
//       // If the rejection reason is empty, show an error message
//       toast.error("Please provide a reason for rejection.");
//       return; // Prevent submission if the reason is empty
//     }

//     try {
//       const response = await fetch(
//         `buildApiUrl("/api/orderItem/accept-reject/${orderToReject}`,
//         {
//           method: "PUT",
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify({ status: "Reject", reason: rejectReason }), // Send the reason
//         }
//       );

//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(errorData.message || "Failed to reject order");
//       }

//       setOrders((prevOrders) =>
//         prevOrders.map((order) =>
//           order._id === orderToReject ? { ...order, status: "Reject" } : order
//         )
//       );

//       toast.success("Order rejected successfully!");
//       setIsRejectModalOpen(false); // Close the modal after rejection
//       setRejectReason(""); // Clear the reason input
//     } catch (error) {
//       toast.error("Error: " + error.message);
//     }
//   };

//   const closeRejectModal = () => {
//     setIsRejectModalOpen(false);
//     setRejectReason(""); // Clear the reason input when modal is closed
//   };

//   const orderCounts = {
//     all: orders.length,
//     pending: orders.filter((order) => order.status?.toLowerCase() === "pending")
//       .length,
//     accept: orders.filter(
//       (order) =>
//         order.status?.toLowerCase() === "accept" &&
//         order.orderDeliveredStatus != "Dispatched"
//     ).length,
//     reject: orders.filter((order) => order.status?.toLowerCase() === "reject")
//       .length,
//     waitingForTpt: orders.filter(
//       (order) =>
//         order.status === "Accept" &&
//         order.orderDeliveredStatus === "Dispatched" &&
//         order.tpt?.tptStatus === "Pending"
//     ).length,
//     dispatched: orders.filter(
//       (order) =>
//         order.status === "Accept" &&
//         order.orderDeliveredStatus === "Dispatched" &&
//         order.tpt?.tptStatus !== "Pending"
//     ).length,
//   };

//   // Pagination Logic
//   const indexOfLastOrder = currentPage * ordersPerPage;
//   const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
//   const currentOrders = sortedOrders.slice(indexOfFirstOrder, indexOfLastOrder); // Use sorted orders for pagination

//   // Calculate total pages
//   const totalPages = Math.ceil(sortedOrders.length / ordersPerPage);

//   // if (loading) {
//   //   return <div>Loading...</div>;
//   // }

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center min-h-screen">
//         <ColorRing
//           visible={true}
//           height="120"
//           width="120"
//           ariaLabel="color-ring-loading"
//           wrapperStyle={{}}
//           wrapperClass="color-ring-wrapper"
//           colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
//         />
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div
//         className="flex items-center max-w-md mx-auto my-4 p-4 bg-red-100 border-l-4 border-red-500 text-red-700 rounded shadow-md"
//         role="alert"
//       >
//         {/* Error Icon */}
//         <svg
//           className="w-6 h-6 mr-3 flex-shrink-0"
//           fill="none"
//           stroke="currentColor"
//           viewBox="0 0 24 24"
//         >
//           <path
//             strokeLinecap="round"
//             strokeLinejoin="round"
//             strokeWidth="2"
//             d="M18.364 5.636l-12.728 12.728M5.636 5.636l12.728 12.728"
//           />
//         </svg>

//         {/* Error Message */}
//         <div>
//           <p className="font-bold">Error</p>
//           <p className="text-sm">{error}</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="bg-white rounded-lg shadow-lg p-4">
//       <div
//         className=" sm:max-h-[100vh] overflow-y-auto"
//         style={{ scrollbarWidth: "none" }}
//       >
//         <div className="sticky top-0 z-30 bg-white py-1 sm:shadow-md">
//           <div className="flex justify-between items-center mb-4 ">
//             <h2 className="text-2xl font-bold text-gray-800">Orders</h2>
//             <button
//               onClick={openModal}
//               className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200"
//             >
//               Create Order
//             </button>
//           </div>

//           <div className="flex flex-col sm:flex-row items-center justify-between mb-4">
//             <div className="flex flex-wrap  gap-2 mb-4 sm:mb-0">
//               {[
//                 {
//                   id: "all",
//                   label: "All",
//                   count: orderCounts.all,
//                   color: "blue",
//                 },
//                 {
//                   id: "pending",
//                   label: "Pending",
//                   count: orderCounts.pending,
//                   color: "yellow",
//                 },
//                 {
//                   id: "accept",
//                   label: "Accepted",
//                   count: orderCounts.accept,
//                   color: "green",
//                 },
//                 {
//                   id: "reject",
//                   label: "Reject",
//                   count: orderCounts.reject,
//                   color: "red",
//                 },
//                 {
//                   id: "waitingForTpt",
//                   label: "Waiting for TPT",
//                   count: orderCounts.waitingForTpt,
//                   color: "yellow",
//                 },
//                 {
//                   id: "dispatched",
//                   label: "All Clear",
//                   count: orderCounts.dispatched,
//                   color: "green",
//                 },
//               ].map((tab) => (
//                 <button
//                   key={tab.id}
//                   className={`py-2 px-4 rounded-lg text-sm font-medium ${
//                     selectedTab === tab.id
//                       ? `bg-${tab.color}-500 text-white`
//                       : "bg-gray-100 text-gray-700 hover:bg-gray-200"
//                   } transition duration-200`}
//                   onClick={() => setSelectedTab(tab.id)}
//                 >
//                   {tab.label}
//                   <span
//                     className={`ml-2 text-xs bg-${tab.color}-100 text-${tab.color}-800 rounded-full px-2`}
//                   >
//                     {tab.count}
//                   </span>
//                 </button>
//               ))}
//             </div>
//             <SearchBar onSearch={(query) => setSearchQuery(query)} />
//           </div>
//         </div>
//         <div className="overflow-x-auto rounded-md">
//           <table className="table-auto w-full border-collapse hidden sm:table">
//             <tbody>
//               {currentOrders.map((order, orderIndex) => (
//                 <React.Fragment key={order._id}>
//                   <tr
//                     className={`border-b bg-white hover:bg-gray-100 ${
//                       expandedItems[order._id] &&
//                       "!bg-red-50  border border-gray-300"
//                     }`}
//                   >
//                     <td colSpan="9" className="p-3">
//                       <div className="flex justify-between items-center">
//                         <div>
//                           <strong className="text-gray-800">
//                             Order ID:{" "}
//                             <span className="font-semibold text-indigo-600 bg-blue-100 uppercase px-2 py-1 rounded-md">
//                               {order._id.slice(-5)}
//                             </span>
//                           </strong>
//                           {order.totalItemAmount === 0 && (
//                             <span className="bg-red-100 text-red-800 font-semibold ml-2 px-2 py-1 rounded-full text-xs">
//                               Under Processing
//                             </span>
//                           )}
//                           <div className="text-sm text-gray-600 mt-1">
//                             Created By -{" "}
//                             {order.createdBy ? order.createdBy.name : "Unknown"}{" "}
//                             <br />
//                             Assigned To -{" "}
//                             {order.assignedTo
//                               ? order.assignedTo.name
//                               : "Not Assigned"}{" "}
//                             <br />
//                             Total Amount -{order.totalItemAmount}
//                           </div>
//                           {order.rejectionReason && (
//                             <div className="text-sm text-red-600 mt-1">
//                               Rejection Reason -{" "}
//                               <span className="font-bold">
//                                 {order.rejectionReason}
//                               </span>
//                             </div>
//                           )}
//                         </div>
//                         <div className="flex items-center space-x-4">
//                           <div>
//                             <FaFilePdf
//                               size={20}
//                               className="text-red-600 hover:text-red-800 cursor-pointer"
//                             />
//                           </div>
//                           <div
//                             className={`px-3 py-1 rounded-full text-sm ${getActionClass(
//                               order.status
//                             )}`}
//                           >
//                             {order.status}
//                           </div>
//                           {order.totalItemAmount > 0 &&
//                             (order.status === "Pending" ||
//                               order.status === "Reject") && (
//                               <select
//                                 className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-1 text-sm hover:bg-gray-200 transition duration-200"
//                                 onChange={(e) =>
//                                   handleUpdateStatus(order._id, e.target.value)
//                                 }
//                                 defaultValue={order.status}
//                               >
//                                 <option value="" disabled>
//                                   Select Status
//                                 </option>
//                                 <option value="Pending">Pending</option>
//                                 <option value="Accept">Accept</option>
//                                 <option value="Reject">Reject</option>
//                               </select>
//                             )}
//                           <button
//                             onClick={() => openEditModal(order)}
//                             className="text-blue-500 hover:text-blue-700"
//                           >
//                             <FaEdit />
//                           </button>
//                         </div>
//                       </div>
//                     </td>
//                   </tr>

//                   {/* Serial Number Row */}
//                   <tr
//                     className={`border-b border-gray-300 bg-white hover:bg-gray-100 ${
//                       expandedItems[order._id] &&
//                       "!bg-red-50 border border-gray-300"
//                     }`}
//                   >
//                     <td className="p-2">{orderIndex + 1}</td>
//                     <td colSpan="6">
//                       <button
//                         onClick={() => toggleItemRow(order._id)}
//                         className="text-indigo-600 hover:text-indigo-800 text-sm font-medium transform transition duration-200"
//                       >
//                         {expandedItems[order._id]
//                           ? "Hide Details"
//                           : "Show Details"}
//                       </button>
//                     </td>
//                   </tr>

//                   {expandedItems[order._id] && (
//                     <tr className="border border-gray-300">
//                       <td colSpan="7" className="p-0">
//                         <table className="table-auto w-full border-collapse">
//                           <thead>
//                             <tr className="text-left bg-gray-50 text-sm text-gray-600">
//                               <th className="p-3 text-center">#</th>
//                               <th className="p-3 text-center">Category</th>
//                               <th className="p-3 text-center">Product Items</th>
//                               <th className="p-3 text-center">Quantity</th>
//                               <th className="p-3 text-center">
//                                 Warehouse Name
//                               </th>
//                               <th className="p-3 text-center">
//                                 Warehouse Batch
//                               </th>
//                               <th className="p-3 text-center">Price</th>
//                               <th className="p-3 text-center">Total Amount</th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {order.items.map((item, itemIndex) => (
//                               <tr key={item._id} className="border-b text-sm">
//                                 <td className="px-2 py-1 text-center"></td>
//                                 <td className="px-2 py-1 text-center">
//                                   {item.categoryName || "Unknown"}
//                                 </td>
//                                 <td className="px-2 py-1 text-center">
//                                   {item.itemName || "Unknown"}
//                                 </td>
//                                 <td className="px-2 py-1 text-center">
//                                   {item.qty || "Unknown"}
//                                 </td>
//                                 <td className="px-2 py-1 text-center">
//                                   {item.wareHouseName || "Unknown"}
//                                 </td>
//                                 <td className="px-2 py-1 text-center">
//                                   {item.inwardReference || "Unknown"}
//                                 </td>
//                                 <td className="px-2 py-1 text-center">
//                                   {item.price ? `${item.price}` : "Unknown"}
//                                 </td>
//                                 <td className="px-2 py-1 text-center">
//                                   {item.totalAmount
//                                     ? `${item.totalAmount}`
//                                     : "Unknown"}
//                                 </td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </table>
//                       </td>
//                     </tr>
//                   )}
//                 </React.Fragment>
//               ))}
//             </tbody>
//           </table>

//           <div className="block sm:hidden">
//             {currentOrders.map((order) => (
//               <MobileOrderCard
//                 key={order._id}
//                 order={order}
//                 getActionClass={getActionClass}
//                 openEditModal={openEditModal}
//                 handleDeleteOrder={handleDeleteOrder}
//                 handleUpdateStatus={handleUpdateStatus}
//               />
//             ))}
//           </div>
//         </div>
//       </div>

//       {isRejectModalOpen && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
//           <div className="bg-white p-6 rounded-lg w-1/3">
//             <h3 className="text-xl font-semibold mb-4">
//               Provide a Reason for Rejection
//             </h3>
//             <textarea
//               value={rejectReason}
//               onChange={(e) => setRejectReason(e.target.value)}
//               placeholder="Enter rejection reason..."
//               className="w-full p-2 border border-gray-300 rounded-lg mb-4"
//             />
//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={closeRejectModal}
//                 className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition duration-200"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleRejectOrder}
//                 className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200"
//               >
//                 Reject Order
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <Pagination
//         currentPage={currentPage}
//         totalPages={totalPages}
//         setCurrentPage={setCurrentPage}
//       />
//       <ToastContainer />
//       {isModalOpen && (
//         <CreateOrderModal
//           closeModal={closeModal}
//           onOrderCreated={handleOrderCreated}
//         />
//       )}
//       {isEditModalOpen && (
//         <EditOrderModal
//           order={orderToEdit}
//           closeModal={closeEditModal}
//           onOrderUpdated={handleOrderCreated}
//         />
//       )}
//     </div>
//   );
// };

// export default OrdersTable;
